import { Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomButton from '../../../components/button/ButtonComponent';
import { initiateUpdatePhone, verifyUpdatePhoneOtp } from '../../../services/login-service';
import { addPhoneNumber, resendPhoneOtp, verifyPhoneOtp } from '../../../services/user-service';
import { hideLoader } from '../../../store/reducers/appSlice';
import { defaultTimerCount } from '../../../utils/helper';
import { useTranslation } from '../../../utils/translations';

import { SecurityCodeStep } from './SecurityCodeStep';
import {
    LOGIN_LIMITS,
    extractPhoneDigits,
    formatPhoneForDisplay,
    normalizeEmail,
    validateContractId,
    validateEmail,
    validatePhone,
} from './validators';

type UpdatePhoneStep = 'identify' | 'email-otp' | 'new-phone' | 'sms-otp';

interface UpdatePhoneFlowProps {
    portalId: number;
    onExit: () => void;
    onSuccess: () => void;
    showToast: (message: string | undefined, severity: string) => void;
}

const OTP_EXPIRY_MINUTES = 5;
const PHONE_EXTENSION = '+1';
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2.5,
        backgroundColor: 'grey.100',
        '& fieldset': { borderColor: 'transparent' },
    },
};

// api_service rejects with the response body (a plain object), so check message and data.message.
const errorMessage = (err: unknown, fallback: string): string => {
    const e = err as { data?: { message?: string | string[] }; message?: string | string[] };
    const raw = e?.data?.message || e?.message;
    const msg = Array.isArray(raw) ? raw[0] : raw;
    return msg || fallback;
};

/**
 * Recovery flow for customers whose phone number is wrong/missing: email + contract ID, email passcode,
 * then add and verify a new number using the short-lived token from the passcode step. The token lives
 * only in this component's state; it is never stored as a login session.
 */
export const UpdatePhoneFlow: React.FC<UpdatePhoneFlowProps> = ({ portalId, onExit, onSuccess, showToast }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [step, setStep] = useState<UpdatePhoneStep>('identify');
    const [email, setEmail] = useState('');
    const [contractId, setContractId] = useState('');
    const [phoneDigits, setPhoneDigits] = useState('');
    const [token, setToken] = useState('');
    const [touched, setTouched] = useState(false);

    const emailError = touched ? validateEmail(email) : null;
    const contractError = touched ? validateContractId(contractId) : null;
    const phoneError = touched ? validatePhone(phoneDigits) : null;

    // Runs an API step: resolves true only on a 200; otherwise toasts the problem. A 200 with
    // data.otpSent === false means the SMS/email provider failed, so it counts as a failure too.
    const run = async (
        call: () => Promise<{ statusCode?: number; message?: string; data?: { otpSent?: boolean } }>,
    ) => {
        try {
            const response = await call();
            if (response?.statusCode === 200 && response.data?.otpSent !== false) return true;
            showToast(response?.message || t('authFlow.updatePhone.genericError'), 'error');
        } catch (err) {
            showToast(errorMessage(err, t('authFlow.updatePhone.genericError')), 'error');
        } finally {
            dispatch(hideLoader());
        }
        return false;
    };

    const sendEmailCode = () =>
        run(
            () =>
                initiateUpdatePhone({
                    email: normalizeEmail(email),
                    contractId: contractId.trim(),
                    portalId,
                }) as Promise<{ statusCode?: number; message?: string }>,
        );

    const handleIdentify = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (validateEmail(email) || validateContractId(contractId)) return;
        if (await sendEmailCode()) {
            setTouched(false);
            setStep('email-otp');
        }
    };

    const handleVerifyEmailOtp = async (otp: string) => {
        let issued = '';
        const ok = await run(async () => {
            const response = await verifyUpdatePhoneOtp({ email: normalizeEmail(email), otp: Number(otp), portalId });
            issued = String((response as { data?: { token?: string } })?.data?.token ?? '');
            // A 200 without a token can't continue the flow.
            return issued ? response : { statusCode: 500, message: t('authFlow.updatePhone.genericError') };
        });
        if (ok) {
            setToken(issued);
            setStep('new-phone');
        }
    };

    const handleAddPhone = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (validatePhone(phoneDigits)) return;
        const ok = await run(() =>
            addPhoneNumber({ phoneNumber: phoneDigits, phoneExtension: PHONE_EXTENSION, isPrimary: 1 }, true, token),
        );
        if (ok) {
            setTouched(false);
            setStep('sms-otp');
        }
    };

    const handleVerifySms = async (otp: string) => {
        const ok = await run(() =>
            verifyPhoneOtp(
                { phoneNumber: phoneDigits, otp: Number(otp), phoneExtension: PHONE_EXTENSION, isPrimary: 1 },
                true,
                token,
            ),
        );
        if (ok) {
            setToken('');
            onSuccess();
        }
    };

    const handleResendSms = async () => {
        await run(() => resendPhoneOtp({ phoneNumber: phoneDigits, phoneExtension: PHONE_EXTENSION }, true, token));
    };

    if (step === 'email-otp') {
        return (
            <SecurityCodeStep
                title={t('authFlow.updatePhone.title')}
                description={t('authFlow.updatePhone.emailOtpBody')}
                expiryMinutes={OTP_EXPIRY_MINUTES}
                resendCooldownSeconds={defaultTimerCount}
                onVerify={handleVerifyEmailOtp}
                onResend={async () => {
                    await sendEmailCode();
                }}
                onCancel={onExit}
            />
        );
    }

    if (step === 'sms-otp') {
        return (
            <SecurityCodeStep
                title={t('authFlow.verifyMobile.title')}
                description={t('authFlow.verifyMobile.body').replace('{{last4}}', phoneDigits.slice(-4))}
                expiryMinutes={OTP_EXPIRY_MINUTES}
                resendCooldownSeconds={defaultTimerCount}
                onVerify={handleVerifySms}
                onResend={handleResendSms}
                onCancel={onExit}
            />
        );
    }

    if (step === 'new-phone') {
        return (
            <Stack component="form" noValidate onSubmit={handleAddPhone} alignItems="center" gap={2} width="100%">
                <Typography variant="h5" component="h1" textAlign="center">
                    {t('authFlow.updatePhone.newPhoneTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    {t('authFlow.updatePhone.newPhoneSubtitle')}
                </Typography>
                <TextField
                    fullWidth
                    value={formatPhoneForDisplay(phoneDigits)}
                    placeholder={t('authFlow.updatePhone.newPhoneLabel')}
                    error={!!phoneError}
                    helperText={phoneError ?? undefined}
                    onChange={e => setPhoneDigits(extractPhoneDigits(e.target.value))}
                    autoComplete="tel-national"
                    inputProps={{ 'aria-label': t('authFlow.updatePhone.newPhoneLabel'), inputMode: 'tel' }}
                    sx={fieldSx}
                />
                <CustomButton type="submit" variant="contained">
                    {`${t('authFlow.updatePhone.sendCode')} →`}
                </CustomButton>
                <CustomButton variant="text" onClick={onExit}>
                    {t('authFlow.updatePhone.back')}
                </CustomButton>
            </Stack>
        );
    }

    return (
        <Stack component="form" noValidate onSubmit={handleIdentify} alignItems="center" gap={2} width="100%">
            <Typography variant="h5" component="h1" textAlign="center">
                {t('authFlow.updatePhone.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
                {t('authFlow.updatePhone.subtitle')}
            </Typography>
            <TextField
                fullWidth
                value={email}
                placeholder={t('authFlow.updatePhone.emailLabel')}
                error={!!emailError}
                helperText={emailError ?? undefined}
                onChange={e => setEmail(e.target.value.slice(0, LOGIN_LIMITS.EMAIL_MAX_LENGTH))}
                autoComplete="email"
                inputProps={{ 'aria-label': t('authFlow.updatePhone.emailLabel') }}
                sx={fieldSx}
            />
            <TextField
                fullWidth
                value={contractId}
                placeholder={t('authFlow.updatePhone.contractLabel')}
                error={!!contractError}
                helperText={contractError ?? undefined}
                onChange={e => setContractId(e.target.value.slice(0, LOGIN_LIMITS.CONTRACT_ID_MAX_LENGTH))}
                inputProps={{
                    'aria-label': t('authFlow.updatePhone.contractLabel'),
                    maxLength: LOGIN_LIMITS.CONTRACT_ID_MAX_LENGTH,
                }}
                sx={fieldSx}
            />
            <CustomButton type="submit" variant="contained">
                {`${t('authFlow.updatePhone.continue')} →`}
            </CustomButton>
            <CustomButton variant="text" onClick={onExit}>
                {t('authFlow.updatePhone.back')}
            </CustomButton>
        </Stack>
    );
};
